import { ContentMedia, MediaSettings } from '@/client/courses';
import { FlexContainer } from '@/ui/styled-ui';
import { CogIcon } from '@/utils/helpers';
import { isVideoFile } from '@/utils/helpers/content-media.helper';
import { InputSwitch } from 'primereact/inputswitch';
import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledImageSettingsContainer = styled(FlexContainer)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: var(--white-main);
  z-index: 1000;
  flex-direction: column;
  gap: var(--small-padding);
  width: 100%;
  padding: var(--default-padding);
  border-radius: var(--xsmall-border-radius);

  p {
    font-size: var(--medium-font-size);
    line-height: var(--medium-line-height);
    font-weight: 500;
    color: var(--black-main);
    margin: 0;
  }
`;

const CloseButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  width: var(--small-button-height);
  height: var(--small-button-height);
  cursor: pointer;

  &:hover {
    i {
      color: var(--red-main);
    }
  }
`;

const StyledImageSettingsButton = styled(FlexContainer)`
  color: var(--black-main);
  font-weight: 500;
  font-size: var(--xsmall-font-size);
  line-height: var(--xsmall-line-height);
  cursor: pointer;
  gap: var(--xxsmall-padding);

  svg {
    transition: all 0.6s ease;
  }

  &:hover {
    svg {
      path {
        stroke: var(--red-main);
      }
      transform: rotate(180deg);
    }
  }

  i {
    font-size: var(--xsmall-font-size);
  }
`;

const StyledSettingsOption = styled(FlexContainer)`
  justify-content: flex-start;
  align-items: center;
  padding: 0;
  width: 100%;
  gap: var(--xsmall-padding);
`;

const StyledSettingsLabel = styled.label`
  font-size: var(--xsmall-font-size);
  line-height: var(--xsmall-line-height);
  font-weight: 500;
  color: var(--black-main);
  margin: 0;
`;

const StyledSettingsHint = styled.span`
  font-size: var(--xsmall-font-size);
  line-height: var(--xsmall-line-height);
  color: var(--gray-text);
`;

type MediaSettingsPanelProps = {
  media: ContentMedia;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  onChange: (settings: MediaSettings) => void;
};

export const MediaSettingsPanel: React.FC<MediaSettingsPanelProps> = ({
  media,
  isOpen,
  onOpen,
  onClose,
  onChange,
}) => {
  const { t } = useTranslation();
  const switchId = useId();
  const isVideo = isVideoFile(media.file);
  const title = isVideo
    ? t('content.video.settings')
    : t('content.image.settings');

  if (isOpen) {
    return (
      <StyledImageSettingsContainer>
        <FlexContainer
          justify="space-between"
          align="center"
          style={{ marginBottom: 'var(--medium-padding)' }}
        >
          <p>{title}</p>
          <CloseButton onClick={onClose}>
            <i className="pi pi-times" />
          </CloseButton>
        </FlexContainer>
        {isVideo ? (
          <>
            <StyledSettingsOption>
              <InputSwitch
                className="v2"
                inputId={`${switchId}-controls`}
                checked={!!media.withControls}
                onChange={(e) => onChange({ withControls: e.value })}
              />
              <StyledSettingsLabel htmlFor={`${switchId}-controls`}>
                {t('content.video.upload.enable.controls')}
              </StyledSettingsLabel>
            </StyledSettingsOption>
            {!media.withControls && (
              <StyledSettingsHint>
                {t('content.video.upload.controls.off.info')}
              </StyledSettingsHint>
            )}
          </>
        ) : (
          <>
            <StyledSettingsOption>
              <InputSwitch
                className="v2"
                inputId={`${switchId}-border`}
                checked={!!media.withBorder}
                onChange={(e) => onChange({ withBorder: e.value })}
              />
              <StyledSettingsLabel htmlFor={`${switchId}-border`}>
                {t('content.image.upload.add.border')}
              </StyledSettingsLabel>
            </StyledSettingsOption>
            <StyledSettingsOption>
              <InputSwitch
                className="v2"
                inputId={`${switchId}-zoom`}
                checked={!!media.withZoom}
                onChange={(e) => onChange({ withZoom: e.value })}
              />
              <StyledSettingsLabel htmlFor={`${switchId}-zoom`}>
                {t('content.image.upload.add.zoom')}
              </StyledSettingsLabel>
            </StyledSettingsOption>
          </>
        )}
      </StyledImageSettingsContainer>
    );
  }

  return (
    <StyledImageSettingsButton onClick={onOpen}>
      <CogIcon />
      <span>{title}</span>
    </StyledImageSettingsButton>
  );
};
